import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  CloudUpload,
  Eye,
  File as FileIcon,
  FileImage,
  FileText,
  Loader2,
  Lock,
  MoreHorizontal,
  PenLine,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { useFileUpload } from '../../../../hooks/useFileUpload';
import { useDeleteClaimFile, useGetPresignedClaimFile } from '../../../../services/claims';
import RenameFileModal from '../../../fileBrowser/components/RenameFileModal';
import { FILE_BROWSER_FOLDERS, getFileFolderPath } from '../../../fileBrowser/fileFolderUtils';
import { EYEBROW, SIDE_CARD } from './SideCards';

// The client's documents (Nic 2026-10-10: the staff file browser looked out
// of place). Drop or pick files, see them upload, open any of them. A file
// can be opened, replaced, renamed or deleted unless it's locked (the same
// rules as the staff browser). Staff-only folders never show here.

const CLIENT_FOLDERS = FILE_BROWSER_FOLDERS.filter((f) => typeof f.role === 'undefined');

const kindOf = (name) => {
  const ext = String(name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return { label: 'PDF', icon: FileText, tone: 'bg-rose-50 text-rose-600 dark:!bg-rose-500/15 dark:text-rose-300' };
  if (['jpg', 'jpeg', 'png', 'heic', 'heif', 'gif', 'webp'].includes(ext)) {
    return { label: 'Photo', icon: FileImage, tone: 'bg-sky-50 text-sky-600 dark:!bg-sky-500/15 dark:text-sky-300' };
  }
  if (['doc', 'docx'].includes(ext)) {
    return { label: 'Word', icon: FileText, tone: 'bg-indigo-50 text-indigo-600 dark:!bg-indigo-500/15 dark:text-indigo-300' };
  }
  return { label: ext ? ext.toUpperCase() : 'File', icon: FileIcon, tone: 'bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-300' };
};

// A small menu for the less common actions. Closes on a click outside or Escape.
const RowMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="More"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex size-10 items-center justify-center rounded-xl text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:hover:!bg-white/10 dark:hover:text-white"
      >
        <MoreHorizontal size={18} />
      </button>
      {open ? (
        <div className="absolute right-0 top-[calc(100%+4px)] z-30 w-48 overflow-hidden rounded-xl bg-white py-1 shadow-[0_12px_40px_rgba(15,23,42,0.18)] ring-1 ring-slate-200 dark:!bg-slate-800 dark:ring-white/10">
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              disabled={it.disabled}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
              className={cn(
                'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-medium outline-none hover:bg-slate-50 focus:outline-none disabled:opacity-40 dark:hover:!bg-white/5',
                it.danger ? 'text-rose-600 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200',
              )}
            >
              <it.icon size={15} /> {it.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// "What is this?" (Nic 2026-10-23): before files upload, the client picks
// what they are from their own checklist, or Something else. No typing; the
// specialist checks a Something else. The pick lands the file on that step
// for their specialist, the same as a checklist upload. Only while they have
// a checklist (onboarding); otherwise files upload straight away.
const WhatIsThis = ({ files, items, onPick, onCancel }) => {
  const first = useRef(null);
  useEffect(() => {
    first.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const names = files.map((x) => x.name);
  const choices = [...items.map((i) => ({ key: i.key, label: i.label })), { key: 'other', label: 'Something else' }];
  return (
    <section
      role="dialog"
      aria-label="What is this?"
      className="rounded-2xl bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] ring-1 ring-sky-200 dark:!bg-slate-900 dark:ring-sky-500/30 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-bold text-slate-900 dark:text-white">What is this?</div>
          <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
            {names.length === 1 ? names[0] : `${names.length} files: ${names.join(', ')}`}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel this upload"
          className="flex size-9 shrink-0 items-center justify-center rounded-xl text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:hover:!bg-white/10 dark:hover:text-white"
        >
          <X size={17} />
        </button>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {choices.map((choice, n) => (
          <button
            key={choice.key}
            ref={n === 0 ? first : undefined}
            type="button"
            onClick={() => onPick(choice.key)}
            className={cn(
              'flex min-h-[44px] items-center rounded-xl px-3.5 py-2.5 text-left text-sm font-semibold outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
              choice.key === 'other'
                ? 'bg-slate-50 text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10'
                : 'bg-white text-slate-800 ring-1 ring-slate-200 hover:bg-sky-50 hover:ring-sky-300 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10 dark:hover:!bg-sky-500/10',
            )}
          >
            {choice.label}
          </button>
        ))}
      </div>
    </section>
  );
};

const ClientDocuments = ({ claimId, files = [], onRefresh, items = [] }) => {
  const input = useRef(null);
  const replaceInput = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [folder, setFolder] = useState('');
  const [replacing, setReplacing] = useState(null);
  const [renaming, setRenaming] = useState(null);
  // Files waiting on "What is this?".
  const [pendingFiles, setPendingFiles] = useState(null);
  const refresh = () => typeof onRefresh === 'function' && onRefresh();

  const { busy, current, total, startUpload } = useFileUpload(claimId, refresh);
  const delFile = useDeleteClaimFile(() => {
    toast.success('Deleted.');
    refresh();
  });
  const openFile = useGetPresignedClaimFile((res) => window.open(res.url, '_blank'));

  const send = async (list, item = null) => {
    const meta = item ? { onboardingItem: item, uploadVia: 'documents' } : { uploadVia: 'documents' };
    const { uploaded, failed } = await startUpload(list, null, null, meta);
    if (uploaded && !failed) toast.success(uploaded === 1 ? 'Uploaded. Thank you!' : `${uploaded} files uploaded. Thank you!`);
    else if (uploaded && failed) toast.warning(`${uploaded} uploaded, ${failed} didn't go through. Please try those again.`);
    else if (failed) toast.error("That upload didn't go through. Please try again.");
  };
  const upload = (list) => {
    if (!list || !list.length) return;
    if (items.length) setPendingFiles(Array.from(list));
    else send(list);
  };
  const pick = (item) => {
    const list = pendingFiles;
    setPendingFiles(null);
    if (list?.length) send(list, item);
  };
  const cancelPick = useCallback(() => setPendingFiles(null), []);

  // Newest first, and only what the client should see.
  const visible = useMemo(
    () =>
      files
        .filter((f) => {
          const path = getFileFolderPath(f, FILE_BROWSER_FOLDERS);
          return !FILE_BROWSER_FOLDERS.some((x) => x.role === 'admin' && x.path === path);
        })
        .sort((a, b) => new Date(b.fileLastUpdated || 0) - new Date(a.fileLastUpdated || 0)),
    [files],
  );
  const folderOf = (f) => getFileFolderPath(f, CLIENT_FOLDERS) || 'root';
  const folders = CLIENT_FOLDERS.map((f) => ({ ...f, count: visible.filter((x) => folderOf(x) === f.path).length })).filter(
    (f) => f.count > 0,
  );
  const shown = folder ? visible.filter((f) => folderOf(f) === folder) : visible;

  return (
    <div className="space-y-4">
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          upload(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={replaceInput}
        type="file"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f || !replacing) return;
          const { uploaded } = await startUpload(f, replacing.fileId, null);
          setReplacing(null);
          if (uploaded) toast.success('Replaced with your new version.');
        }}
      />

      {pendingFiles ? (
        <WhatIsThis files={pendingFiles} items={items} onPick={pick} onCancel={cancelPick} />
      ) : (
        // Drop zone (a tap opens the phone's picker: camera, photos, files).
        <button
        type="button"
        disabled={busy}
        onClick={() => input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          upload(e.dataTransfer?.files);
        }}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-5 py-7 text-center outline-none transition-[background-color,border-color] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 sm:py-9',
          dragging
            ? 'border-sky-400 bg-sky-50 dark:!bg-sky-500/10'
            : 'border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50/60 dark:border-white/15 dark:!bg-slate-900 dark:hover:!bg-white/5',
        )}
      >
        {busy ? (
          <>
            <Loader2 size={28} className="animate-spin text-sky-600" />
            <div className="text-sm font-bold text-slate-900 dark:text-white">
              Uploading{total > 1 ? ` ${current} of ${total}` : ''}...
            </div>
            <div className="h-1.5 w-48 overflow-hidden rounded-full bg-slate-100 dark:!bg-white/10">
              <div
                className="h-full rounded-full bg-sky-500 transition-[width]"
                style={{ width: `${total ? Math.round(((current - 0.5) / total) * 100) : 30}%` }}
              />
            </div>
          </>
        ) : (
          <>
            <span className="flex size-12 items-center justify-center rounded-2xl bg-slate-900 text-white dark:!bg-white dark:!text-slate-900">
              <CloudUpload size={24} />
            </span>
            <div className="text-[15px] font-bold text-slate-900 dark:text-white">
              <span className="sm:hidden">Tap to upload</span>
              <span className="hidden sm:inline">Drop files here, or click to upload</span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              PDFs and photos both work. A clear photo from your phone is perfect.
            </div>
          </>
        )}
      </button>
      )}

      <section className={cn(SIDE_CARD, '!p-0')}>
        <div className="flex flex-wrap items-center gap-2 px-[18px] pb-2 pt-4 sm:px-[20px]">
          <div className={EYEBROW}>
            Your files <span className="ml-1 tabular-nums text-slate-400">{visible.length}</span>
          </div>
        </div>
        {/* Folders, only when files are in them. */}
        {folders.length ? (
          <div className="os-scroll -mx-0 flex gap-1.5 overflow-x-auto px-[18px] pb-2 sm:px-[20px]">
            {[{ path: '', label: 'All', count: visible.length }, ...folders].map((f) => (
              <button
                key={f.path || 'all'}
                type="button"
                onClick={() => setFolder(f.path)}
                className={cn(
                  'inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-semibold outline-none focus:outline-none',
                  folder === f.path
                    ? 'bg-slate-900 text-white dark:!bg-white dark:!text-slate-900'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200/70 dark:!bg-white/10 dark:text-slate-300',
                )}
              >
                {f.label}
                <span className="tabular-nums opacity-60">{f.count}</span>
              </button>
            ))}
          </div>
        ) : null}

        {shown.length === 0 ? (
          <div className="px-5 pb-8 pt-4 text-center">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">No files yet</div>
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Anything you upload shows up here.</div>
          </div>
        ) : (
          <ul className="mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/[0.06]">
            {shown.map((f) => {
              const kind = kindOf(f.fileName);
              const locked = !!f.fileLocked;
              // Ours (Nic 2026-10-21): the signed agreement, Medical Opinions,
              // DBQ packets. View only; the server refuses the rest too.
              const readOnly = f.fileEditable === false;
              const pending = f.fileVersion === 0;
              const folderLabel = CLIENT_FOLDERS.find((x) => x.path === folderOf(f))?.label;
              return (
                <li key={f.fileId} className="flex items-center gap-3 px-[18px] py-3 sm:px-[20px]">
                  <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-xl', kind.tone)}>
                    <kind.icon size={19} />
                  </span>
                  <button
                    type="button"
                    disabled={pending || locked}
                    onClick={() => openFile.mutate({ claimId, fileId: f.fileId })}
                    className="min-w-0 flex-1 text-left outline-none focus:outline-none"
                  >
                    <div className="truncate text-sm font-semibold text-slate-800 hover:text-sky-700 dark:text-slate-100 dark:hover:text-sky-300">
                      {f.fileName}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-slate-500 dark:text-slate-400">
                      <span>{kind.label}</span>
                      {f.fileLastUpdated ? (
                        <>
                          <span aria-hidden>·</span>
                          <span>
                            {/* fileLastUpdated is a plain DATE sent as UTC midnight: read it in UTC
                               or it shows a day early in the US (QA 2026-10-26). */}
                            {new Date(f.fileLastUpdated).toLocaleDateString('en-US', {
                              timeZone: 'UTC',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        </>
                      ) : null}
                      {folderLabel ? (
                        <>
                          <span aria-hidden>·</span>
                          <span>{folderLabel}</span>
                        </>
                      ) : null}
                      {locked ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-slate-500">
                          <Lock size={11} /> Locked
                        </span>
                      ) : null}
                    </div>
                  </button>
                  <button
                    type="button"
                    disabled={pending || locked}
                    onClick={() => openFile.mutate({ claimId, fileId: f.fileId })}
                    className="hidden h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl bg-white px-3.5 text-sm font-semibold text-slate-700 outline-none ring-1 ring-slate-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-40 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10 sm:inline-flex"
                  >
                    <Eye size={15} /> View
                  </button>
                  <RowMenu
                    items={[
                      { label: 'View', icon: Eye, onClick: () => openFile.mutate({ claimId, fileId: f.fileId }), disabled: pending || locked },
                      ...(readOnly
                        ? []
                        : [
                            {
                              label: 'Replace',
                              icon: RefreshCw,
                              disabled: pending || locked,
                              onClick: () => {
                                setReplacing(f);
                                replaceInput.current?.click();
                              },
                            },
                            { label: 'Rename', icon: PenLine, disabled: locked, onClick: () => setRenaming(f) },
                            {
                              label: 'Delete',
                              icon: Trash2,
                              danger: true,
                              disabled: pending || locked,
                              onClick: () => {
                                if (window.confirm(`Delete ${f.fileName}?`)) delFile.mutate({ claimId, fileId: f.fileId });
                              },
                            },
                          ]),
                    ]}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <RenameFileModal
        show={!!renaming}
        toggle={() => setRenaming(null)}
        file={renaming}
        claimId={claimId}
        onSuccess={() => {
          toast.success('Renamed.');
          refresh();
        }}
      />
    </div>
  );
};

export default ClientDocuments;
